import type { AxiosError } from "axios";
import { ChevronDownIcon, Loader2Icon, SearchIcon, SendIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatEur } from "@/lib/currency";
import { cn } from "@/lib/utils";

import {
  type AvailableCounterparty,
  useAvailableCounterparties,
  useCreatePaymentOrder,
} from "../api/use-payment-orders";
import { parseEurosToCents } from "../lib/currency";

/** Vue getCounterpartyLabel — display name + persisted amount for the select options. */
function getCounterpartyOptionLabel(counterparty: AvailableCounterparty): string {
  const typeName = counterparty.type?.type;
  let name = "—";

  if (typeName === "PASSENGER") {
    const users = counterparty.familyClaimAcquisition?.family?.usersClaims;
    if (users?.length) {
      name = users.map((userClaim) => `${userClaim.user.firstName} ${userClaim.user.lastName}`).join(", ");
    } else {
      name = "Passager";
    }
  } else if (typeName === "TRAVEL_AGENCY") {
    name = counterparty.company?.name ?? "Agence de voyage";
  } else if (typeName === "COMPANY") {
    name = counterparty.company?.name ?? "Entreprise";
  } else if (typeName === "UPCLAIM") {
    name = "Upclaim";
  }

  const amount = counterparty.totalAmountToPay
    ? formatEur(counterparty.totalAmountToPay.amount, counterparty.totalAmountToPay.precision)
    : "";

  return amount ? `${name} — ${amount}` : name;
}

interface CreatePaymentOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
  /** When provided, the modal auto-searches the claim on open so the admin doesn't re-enter it. */
  preselectedClaimNumber?: number | null;
  /** When provided along with preselectedClaimNumber, the counterparty is auto-selected. */
  preselectedCounterpartyId?: string | null;
}

/** Port of CreatePaymentOrderModal.vue — two-step (claim search, then counterparty + amount). */
export function CreatePaymentOrderModal({
  open,
  onOpenChange,
  onCreated,
  preselectedClaimNumber,
  preselectedCounterpartyId,
}: CreatePaymentOrderModalProps) {
  // Step 1: claim number
  const [claimNumberInput, setClaimNumberInput] = useState("");
  const [submittedClaimNumber, setSubmittedClaimNumber] = useState<number | null>(null);

  // Step 2: counterparty + amount
  const [selectedCounterpartyId, setSelectedCounterpartyId] = useState("");
  const [amountEuros, setAmountEuros] = useState("");
  const [amountInCents, setAmountInCents] = useState<number | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const counterpartiesQuery = useAvailableCounterparties(submittedClaimNumber);
  const createOrder = useCreatePaymentOrder();

  const counterparties = counterpartiesQuery.data ?? [];
  const selectedCounterparty = counterparties.find(
    (counterparty) => counterparty.id === selectedCounterpartyId,
  );
  const claimId = selectedCounterparty?.claimId ?? "";
  const isValid = Boolean(selectedCounterpartyId && amountInCents && amountInCents > 0);

  const claimError = counterpartiesQuery.isError
    ? ((counterpartiesQuery.error as AxiosError<{ message?: string }>)?.response?.data?.message ??
      "Dossier introuvable")
    : counterpartiesQuery.data && !counterpartiesQuery.data.length
      ? "Aucun bénéficiaire disponible pour ce dossier."
      : null;

  function selectCounterparty(id: string) {
    setSelectedCounterpartyId(id);
    const counterparty = counterparties.find((candidate) => candidate.id === id);

    // Prefer the live total (the breakdown net for PASSENGER counterparties, the
    // financial-health total, commissions included, for the others) over the
    // persisted totalAmountToPay.
    if (counterparty?.liveTotalCents && counterparty.liveTotalCents > 0) {
      setAmountEuros((counterparty.liveTotalCents / 100).toFixed(2).replace(".", ","));
      setAmountInCents(counterparty.liveTotalCents);
    } else if (counterparty?.totalAmountToPay) {
      const euros =
        counterparty.totalAmountToPay.amount / 10 ** (counterparty.totalAmountToPay.precision ?? 2);
      setAmountEuros(euros.toFixed(2).replace(".", ","));
      setAmountInCents(counterparty.totalAmountToPay.amount);
    } else {
      setAmountEuros("");
      setAmountInCents(null);
    }
  }

  function searchCounterparties() {
    const parsed = Number.parseInt(claimNumberInput, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return;
    }
    setSelectedCounterpartyId("");
    setSubmittedClaimNumber(parsed);
  }

  function resetForm() {
    setClaimNumberInput("");
    setSubmittedClaimNumber(null);
    setSelectedCounterpartyId("");
    setAmountEuros("");
    setAmountInCents(null);
    setBreakdownOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  }

  // Auto-load counterparties when opened from a claim context with a preselection.
  useEffect(() => {
    if (open && preselectedClaimNumber) {
      setClaimNumberInput(String(preselectedClaimNumber));
      setSubmittedClaimNumber(preselectedClaimNumber);
    }
  }, [open, preselectedClaimNumber]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectCounterparty reads the freshly loaded list
  useEffect(() => {
    if (open && preselectedCounterpartyId && !selectedCounterpartyId && counterparties.length) {
      const preselected = counterparties.find((candidate) => candidate.id === preselectedCounterpartyId);
      if (preselected) {
        selectCounterparty(preselected.id);
      }
    }
  }, [open, preselectedCounterpartyId, selectedCounterpartyId, counterparties]);

  async function submit() {
    if (!isValid || !amountInCents || !claimId) {
      return;
    }
    try {
      await createOrder.mutateAsync({
        claimId,
        counterpartyId: selectedCounterpartyId,
        amountInCents,
      });
      resetForm();
      onCreated?.();
      onOpenChange(false);
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer un ordre de paiement</DialogTitle>
        </DialogHeader>

        <DialogPanel className="space-y-5">
          {/* Claim number search */}
          <div>
            <Label className="block text-sm font-medium text-gray-700">N° de dossier</Label>
            <div className="mt-1 flex gap-2">
              <Input
                value={claimNumberInput}
                onChange={(event) => setClaimNumberInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    searchCounterparties();
                  }
                }}
                type="text"
                inputMode="numeric"
                placeholder="Ex: 1234"
              />
              <Button
                variant="outline"
                size="icon"
                disabled={counterpartiesQuery.isFetching || !claimNumberInput.trim()}
                onClick={searchCounterparties}
              >
                {counterpartiesQuery.isFetching ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <SearchIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
            {claimError ? <p className="mt-1 text-sm text-red-600">{claimError}</p> : null}
          </div>

          {/* Counterparty dropdown */}
          {counterparties.length ? (
            <div>
              <Label className="block text-sm font-medium text-gray-700">Bénéficiaire</Label>
              <Select
                value={selectedCounterpartyId || null}
                onValueChange={(value) => {
                  if (typeof value === "string") {
                    selectCounterparty(value);
                  }
                }}
              >
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue>
                    {selectedCounterparty
                      ? getCounterpartyOptionLabel(selectedCounterparty)
                      : "Sélectionner un bénéficiaire"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {counterparties.map((counterparty) => (
                    <SelectItem key={counterparty.id} value={counterparty.id}>
                      {getCounterpartyOptionLabel(counterparty)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCounterparty && !selectedCounterparty.bankAccount ? (
                <p className="mt-1 text-xs text-amber-600">
                  Ce bénéficiaire n'a pas encore de coordonnées bancaires.
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Amount */}
          {selectedCounterpartyId ? (
            <div>
              <Label className="block text-sm font-medium text-gray-700">Montant (EUR)</Label>
              <div className="relative mt-1">
                <Input
                  value={amountEuros}
                  onChange={(event) => {
                    setAmountEuros(event.currentTarget.value);
                    setAmountInCents(parseEurosToCents(event.currentTarget.value));
                  }}
                  type="text"
                  inputMode="decimal"
                  placeholder="250,00"
                  className="pr-12"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-sm text-gray-500">EUR</span>
                </div>
              </div>
              {amountInCents ? <p className="mt-1 text-xs text-gray-400">{amountInCents} centimes</p> : null}

              {/* Breakdown expander (only for PASSENGER counterparties) */}
              {selectedCounterparty?.liveBreakdown ? (
                <Collapsible
                  open={breakdownOpen}
                  onOpenChange={setBreakdownOpen}
                  className="mt-3 rounded-md border border-gray-200 bg-gray-50"
                >
                  <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-100">
                    <span>Détail du calcul</span>
                    <ChevronDownIcon
                      className={cn("h-4 w-4 transition-transform", breakdownOpen && "rotate-180")}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t border-gray-200 px-3 py-3">
                    <dl className="space-y-1 text-xs text-gray-700">
                      {selectedCounterparty.liveBreakdown.demands.map((demand) => (
                        <div key={demand.monetaryDemandId} className="flex justify-between">
                          <dt className="truncate">{demand.displayText || demand.type}</dt>
                          <dd className="font-mono">{formatEur(demand.amountCents)}</dd>
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-gray-200 pt-1">
                        <dt>Brut</dt>
                        <dd className="font-mono">
                          {formatEur(selectedCounterparty.liveBreakdown.grossCents)}
                        </dd>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <dt>
                          − Commission Upclaim (
                          {selectedCounterparty.liveBreakdown.commissionRates.upclaimPercentage}%)
                        </dt>
                        <dd className="font-mono">
                          −{formatEur(selectedCounterparty.liveBreakdown.upclaimCommissionCents)}
                        </dd>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <dt>
                          − Commission agence (
                          {selectedCounterparty.liveBreakdown.commissionRates.travelAgencyPercentage}%)
                        </dt>
                        <dd className="font-mono">
                          −{formatEur(selectedCounterparty.liveBreakdown.travelAgencyCommissionCents)}
                        </dd>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900">
                        <dt>Net à payer</dt>
                        <dd className="font-mono">
                          {formatEur(selectedCounterparty.liveBreakdown.netCents)}
                        </dd>
                      </div>
                    </dl>
                  </CollapsibleContent>
                </Collapsible>
              ) : null}
            </div>
          ) : null}
        </DialogPanel>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button disabled={!isValid || createOrder.isPending} onClick={submit}>
            {createOrder.isPending ? (
              <Loader2Icon data-icon="inline-start" className="animate-spin" />
            ) : (
              <SendIcon data-icon="inline-start" />
            )}
            Créer l'ordre de paiement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
